import {
  comparePeakCoughFlowProtocols,
  normalizePeakCoughFlowPayload,
  summarizePeakCoughFlowPayload,
} from '../peak-cough-flow/peakCoughFlowDomain';
import { normalizeSnipPayload, summarizePosturalVitalCapacityPayload, summarizeSnipPayload } from './neuromuscularDomain';

export type NeuromuscularHistoryTestKey = 'PEAK_COUGH_FLOW' | 'CV_POSTURAL' | 'SNIP';
export interface NeuromuscularHistoryEntry { id: string; assessmentDate?: string | null; payload: unknown }
export interface NeuromuscularHistoryRow {
  id: string;
  date: string;
  label: string;
  previous: string;
  current: string;
  delta: string;
  relativeDelta: string;
  comparable: boolean;
  note: string;
  unit: string;
}

type Dict = Record<string, unknown>;
const record = (value: unknown): Dict => value && typeof value === 'object' && !Array.isArray(value) ? value as Dict : {};
const clean = (value: unknown): string => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR') : '';
const number = (value: number, decimals = 1): string => value.toLocaleString('pt-BR', { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
const signed = (value: number, decimals = 1): string => {
  const rounded = Number(value.toFixed(decimals));
  return `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${number(Math.abs(rounded), decimals)}`;
};
const knownEqual = (a: unknown, b: unknown): boolean => typeof a === 'string' && clean(a).length > 0 && clean(a) === clean(b);
const knownBoolean = (a: unknown, b: unknown): boolean => typeof a === 'boolean' && typeof b === 'boolean' && a === b;

export const formatNeuromuscularHistoryDate = (value?: string | null): string => {
  if (!value) return 'Data não informada';
  const plain = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const brazilian = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (plain || brazilian) {
    const [year, month, day] = plain ? [Number(plain[1]), Number(plain[2]), Number(plain[3])] : [Number(brazilian![3]), Number(brazilian![2]), Number(brazilian![1])];
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
      ? `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}` : 'Data não informada';
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString('pt-BR') : 'Data não informada';
};

export const parseNeuromuscularHistoryDate = (value?: string | null): number | null => {
  if (!value || formatNeuromuscularHistoryDate(value) === 'Data não informada') return null;
  const brazilian = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const timestamp = brazilian ? Date.UTC(Number(brazilian[3]), Number(brazilian[2]) - 1, Number(brazilian[1])) : Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const selectPreviousNeuromuscularHistory = (
  history: readonly NeuromuscularHistoryEntry[], currentAssessmentDate?: string | null, limit = 5,
): NeuromuscularHistoryEntry[] => {
  const currentTime = parseNeuromuscularHistoryDate(currentAssessmentDate);
  if (currentAssessmentDate && currentTime == null) return [];
  return history.map((entry) => ({ entry, timestamp: parseNeuromuscularHistoryDate(entry.assessmentDate) }))
    .filter((item): item is { entry: NeuromuscularHistoryEntry; timestamp: number } => item.timestamp != null && (currentTime == null || item.timestamp < currentTime))
    .sort((a, b) => b.timestamp - a.timestamp).slice(0, Math.max(0, limit)).map((item) => item.entry);
};

const makeRow = (
  entry: NeuromuscularHistoryEntry, key: string, label: string, previous: number | null, current: number,
  unit: string, protocol: { comparable: boolean; note?: string }, decimals = 1, percentagePoints = false,
): NeuromuscularHistoryRow => {
  const comparable = protocol.comparable && previous != null;
  return {
    id: `${entry.id}:${key}`, date: formatNeuromuscularHistoryDate(entry.assessmentDate), label,
    previous: previous == null ? 'Sem medida correspondente' : `${number(previous, decimals)} ${unit}`,
    current: `${number(current, decimals)} ${unit}`,
    delta: comparable ? `${signed(current - previous!, decimals)} ${percentagePoints ? 'p.p.' : unit}` : '',
    relativeDelta: comparable && !percentagePoints && previous! > 0 ? `${signed((current - previous!) / previous! * 100)}%` : '',
    comparable,
    note: previous == null ? 'A condição não possui medida correspondente na avaliação anterior.' : protocol.note ?? '',
    unit,
  };
};

const vitalCapacityProtocol = (current: unknown, previous: unknown) => {
  const a = record(current), b = record(previous);
  if (!['slow', 'forced'].includes(String(a.maneuver)) || !['mouthpiece', 'mask'].includes(String(a.interface)) ||
    !knownEqual(a.maneuver, b.maneuver) || !knownEqual(a.interface, b.interface) || !knownEqual(a.device, b.device) || !knownBoolean(a.abdominalSupport, b.abdominalSupport)) {
    return { comparable: false, note: 'Manobra, interface, equipamento ou suporte abdominal diferentes ou não registrados.' };
  }
  return { comparable: true };
};

const snipProtocol = (current: unknown, previous: unknown) => {
  const a = record(current), b = record(previous);
  const sideSet = (input: unknown): string => [...new Set(normalizeSnipPayload(input).attempts.filter((item) => item.acceptable).map((item) => item.nostril))].sort().join(',');
  const sidesKnown = (input: Dict): boolean => Array.isArray(input.attempts) && input.attempts.filter((item) => record(item).acceptable !== false).every((item) => ['left', 'right'].includes(String(record(item).nostril)));
  if (!['seated', 'supine', 'semi_recumbent', 'other'].includes(String(a.position)) || !sidesKnown(a) || !sidesKnown(b) ||
    !knownEqual(a.position, b.position) || !knownEqual(a.device, b.device) || a.initialVolume !== 'FRC' ||
    !knownEqual(a.initialVolume, b.initialVolume) || !knownEqual(a.contralateralNostril, b.contralateralNostril) || !knownBoolean(a.mouthClosed, b.mouthClosed) ||
    (a.angleDegrees ?? null) !== (b.angleDegrees ?? null) || sideSet(a) !== sideSet(b) ||
    (a.position === 'other' && !knownEqual(a.otherPosition, b.otherPosition))) {
    return { comparable: false, note: 'Posição, inclinação, equipamento, volume inicial, narinas ou fechamento da boca diferentes ou não registrados.' };
  }
  if (a.position === 'semi_recumbent' && (a.angleDegrees == null || b.angleDegrees == null)) {
    return { comparable: false, note: 'Inclinação não registrada nas duas avaliações semissentadas.' };
  }
  return { comparable: true };
};

export const buildNeuromuscularHistoryRows = (
  testKey: NeuromuscularHistoryTestKey | string,
  currentPayload: unknown,
  history: readonly NeuromuscularHistoryEntry[] = [],
): NeuromuscularHistoryRow[] => {
  if (testKey === 'PEAK_COUGH_FLOW') {
    const current = summarizePeakCoughFlowPayload(currentPayload);
    const normalizedCurrent = normalizePeakCoughFlowPayload(currentPayload);
    return history.flatMap((entry) => {
      const previous = summarizePeakCoughFlowPayload(entry.payload);
      const normalizedPrevious = normalizePeakCoughFlowPayload(entry.payload);
      return current.series.filter((item) => item.bestFlow != null).map((item) => {
        const prior = previous.series.find((priorItem) => priorItem.condition === item.condition);
        let protocol = comparePeakCoughFlowProtocols(currentPayload, entry.payload, item.condition);
        if (protocol.comparable && item.condition !== 'unassisted') {
          const currentTechnique = normalizedCurrent?.series.find((series) => series.condition === item.condition)?.techniqueNotes;
          const previousTechnique = normalizedPrevious?.series.find((series) => series.condition === item.condition)?.techniqueNotes;
          if (!knownEqual(currentTechnique, previousTechnique)) protocol = { comparable: false, note: 'A técnica de assistência difere ou não foi descrita nas duas avaliações.' };
        }
        return makeRow(entry, item.condition, item.label, prior?.bestFlow ?? null, item.bestFlow!, 'L/min', protocol);
      });
    });
  }
  if (testKey === 'CV_POSTURAL') {
    const current = summarizePosturalVitalCapacityPayload(currentPayload);
    return history.flatMap((entry) => {
      const previous = summarizePosturalVitalCapacityPayload(entry.payload);
      const protocol = vitalCapacityProtocol(currentPayload, entry.payload);
      const rows: NeuromuscularHistoryRow[] = [];
      if (current.bestSeated != null) rows.push(makeRow(entry, 'seated', 'Capacidade vital sentada', previous.bestSeated, current.bestSeated, 'L', protocol, 2));
      if (current.bestSupine != null) rows.push(makeRow(entry, 'supine', 'Capacidade vital supina', previous.bestSupine, current.bestSupine, 'L', protocol, 2));
      if (current.reductionPercent != null) rows.push(makeRow(entry, 'postural_reduction', 'Redução postural sentado–supino', previous.reductionPercent, current.reductionPercent, '%', protocol, 1, true));
      return rows;
    });
  }
  if (testKey === 'SNIP') {
    const current = summarizeSnipPayload(currentPayload);
    if (current.max == null) return [];
    return history.map((entry) => makeRow(entry, 'snip', 'Maior magnitude do SNIP', summarizeSnipPayload(entry.payload).max, current.max!, 'cmH2O', snipProtocol(currentPayload, entry.payload)));
  }
  return [];
};
